import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { useAuth } from '@/hooks/auth';
import OnboardingFlow from './OnboardingFlow';

interface OnboardingWrapperProps {
  children: React.ReactNode;
}

const OnboardingWrapper: React.FC<OnboardingWrapperProps> = ({ children }) => {
  const { user, isLoading: authLoading } = useAuth();
  const { status, isLoading: onboardingLoading, error, isPrivilegedRole } = useOnboarding();
  const location = useLocation();

  // Show loading only when absolutely necessary
  // Don't show loading if we already know the status is completed
  if (authLoading || (!user && !authLoading) || (onboardingLoading && status === null)) {
    return (
      <div className="min-h-screen bg-[#EBECF0] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-3 text-gray-600 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  // If no user, redirect to auth
  // If there was an error fetching onboarding status, do NOT show onboarding, just show dashboard
  if (error) {
    return <>{children}</>;
  }

  // Skip onboarding entirely for recruiters, admins, and superadmins
  if (isPrivilegedRole) {
    return <>{children}</>;
  }

  // If onboarding is completed, show the normal dashboard
  if (status === 'completed') {
    return <>{children}</>;
  }

  // If onboarding is pending or in progress, show onboarding flow
  if (status === 'pending' || status === 'in_progress') {
    // Allow access to onboarding route
    if (location.pathname.startsWith('/onboarding')) {
      return <OnboardingFlow />;
    }
    // Redirect to onboarding for all other dashboard routes
    if (location.pathname.startsWith('/dashboard')) {
      return <Navigate to="/onboarding" replace />;
    }
  }

  // Default case: show children (for non-dashboard routes)
  return <>{children}</>;
};

export default OnboardingWrapper; 